import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { clearErrorMessage } from "@/actions/auth";
import { getCityWhitelabelMappings } from "@/actions/cities";
import {
  setCurrentPageValue,
  setFilterValue,
  setOrder,
  setOrderBy,
  setSearchValue,
} from "@/actions/screen";
import type { City } from "@/apis/city/city-apis";
import * as routes from "@/constants/routes";
import i18n, { LANGUAGE_LABELS, strings } from "@/I18n";
import type { AppDispatch, RootState } from "@/types/redux";
import Header from "@/v2/components/header";
import { TourProvider, useTour } from "@/v2/components/onboarding";
import { EntityPanel } from "@/v2/components/panel/EntityPanel";
import { SidebarCustomizeDrawer } from "@/v2/components/sidebar";
import {
  useAutoSetPreferences,
  usePreviewMode,
  useSidebarPreferences,
} from "@/v2/hooks";
import { useAdminDetails } from "@/v2/hooks/useAuth";
import { useEffectiveWhitelabelId } from "@/v2/hooks/useEffectiveWhitelabelId";
import { useFeatureFlags } from "@/v2/hooks/useFeatureFlags";
import { useOrganizationId } from "@/v2/hooks/useOrganizationId";
import { usePanelUrlSync } from "@/v2/hooks/usePanelUrlSync";
import { useZendeskTheme } from "@/v2/hooks/useZendeskTheme";
import { queryClient } from "@/v2/queries";
import { useCitiesList, useReduxSync, useSavedCity } from "@/v2/stores";
import { BottomNavigation } from "@/v2/ui/composed/navigation/BottomNavigation";
import {
  DashboardRoutes,
  DashboardSidebar,
  PreviewModeBanner,
} from "./components";
import { OnboardingTour } from "./constants";
import {
  useDashboardState,
  useNavigationHandlers,
  useSidebarConfig,
} from "./hooks";
import type { FilterItem, FilterProps, OrderProps } from "./types";

function DashboardContent() {
  const dispatch = useDispatch<AppDispatch>();

  // Sync Redux to Zustand stores
  useReduxSync();
  usePanelUrlSync();

  const errorMessage = useSelector(
    (state: RootState) => state.auth.errorMessage
  );
  const citiesList = useCitiesList() as City[];
  const savedCity = useSavedCity();
  const searchValue = useSelector(
    (state: RootState) => state.screen.searchValue
  );
  const filterValue = useSelector(
    (state: RootState) => state.screen.filterValue
  ) as FilterItem[] | null;
  const currentPageValue = useSelector(
    (state: RootState) => state.screen.currentPageValue
  );
  const order = useSelector((state: RootState) => state.screen.order);
  const orderBy = useSelector((state: RootState) => state.screen.orderBy);

  const adminDetails = useAdminDetails();

  const handleClearErrorMessage = useCallback(
    () => dispatch(clearErrorMessage()),
    [dispatch]
  );
  const handleSetSearchValue = useCallback(
    (value: string) => dispatch(setSearchValue(value)),
    [dispatch]
  );
  const handleSetFilterValue = useCallback(
    (value: FilterItem[] | null) => dispatch(setFilterValue(value)),
    [dispatch]
  );
  const handleSetCurrentPageValue = useCallback(
    (value: number) => dispatch(setCurrentPageValue(value)),
    [dispatch]
  );
  const handleSetOrder = useCallback(
    (value: string) => dispatch(setOrder(value)),
    [dispatch]
  );
  const handleSetOrderBy = useCallback(
    (value: string) => dispatch(setOrderBy(value)),
    [dispatch]
  );

  useZendeskTheme();

  useEffect(() => {
    dispatch(getCityWhitelabelMappings());
  }, [dispatch]);

  const effectiveWhitelabelId = useEffectiveWhitelabelId();
  const featureFlagsOrgId = useOrganizationId(effectiveWhitelabelId);
  const featureFlags = useFeatureFlags(featureFlagsOrgId);
  const { startTour, isTourRunning } = useTour();
  useAutoSetPreferences();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCustomizeDrawerOpen, setIsCustomizeDrawerOpen] = useState(false);
  const [preferences, setPreferences] = useSidebarPreferences();

  const {
    isPreviewMode,
    previewRole,
    previewWhitelabel,
    effectiveIsSuperAdmin,
    effectiveIsCityAdmin,
    exitPreviewMode,
  } = usePreviewMode();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const normalizedPath = location.pathname.replace("/v2", "");

  // Sync URL search param to state
  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch) {
      handleSetSearchValue(urlSearch);
    }
  }, [searchParams, handleSetSearchValue]);

  const currentLanguageLabel =
    LANGUAGE_LABELS[i18n.locale as keyof typeof LANGUAGE_LABELS] || "English";

  const { refreshProps } = useDashboardState({
    setSearchValue: handleSetSearchValue,
    setFilterValue: handleSetFilterValue,
    setOrder: handleSetOrder,
    setOrderBy: handleSetOrderBy,
    setCurrentPageValue: handleSetCurrentPageValue,
    errorMessage,
    clearErrorMessage: handleClearErrorMessage,
    savedCity,
  });

  const { sidebarItemHandlers } = useNavigationHandlers({
    refreshProps,
  });

  const { visibleSidebarConfig, orderedSidebarConfig, isSidebarItemSelected } =
    useSidebarConfig({
      adminDetails,
      strings,
      effectiveIsSuperAdmin,
      effectiveIsCityAdmin,
      routes,
      itemHandlers: sidebarItemHandlers,
      preferences,
      normalizedPath,
      featureFlags,
    });

  const orderProps: OrderProps = useMemo(
    () => ({
      order,
      setOrder: handleSetOrder,
      orderBy,
      setOrderBy: handleSetOrderBy,
    }),
    [order, handleSetOrder, orderBy, handleSetOrderBy]
  );

  const filterProps: FilterProps = useMemo(
    () => ({
      filterValue,
      setFilterValue: handleSetFilterValue,
    }),
    [filterValue, handleSetFilterValue]
  );

  const orderValue = useMemo(
    () => (orderBy === "" ? "" : `${orderBy},${order}`),
    [orderBy, order]
  );

  const clearSearchValue = useCallback(
    () => handleSetSearchValue(""),
    [handleSetSearchValue]
  );

  // Version toggle for UI switching
  const switchVersion = useCallback((pathname: string, search: string) => {
    const newPath = pathname.startsWith("/v2/")
      ? pathname.replace("/v2/", "/")
      : `/v2${pathname}`;
    return `${newPath}${search}`;
  }, []);

  const handleVersionToggle = useMemo(
    () => () => {
      sessionStorage.setItem("preferV1UI", "true");
      navigate(switchVersion(location.pathname, location.search));
    },
    [navigate, switchVersion, location.pathname, location.search]
  );

  const handleCustomizeClick = useCallback(
    () => setIsCustomizeDrawerOpen(true),
    []
  );
  const handleMobileMenuClick = useCallback(
    () => setIsMobileMenuOpen(true),
    []
  );

  return (
    <div>
      <Header
        onMenuClick={handleMobileMenuClick}
        onVersionToggle={
          location.pathname.replace(/\/$/, "") ===
          routes.toV2Route(routes.DASHBOARD_MAP)
            ? undefined
            : handleVersionToggle
        }
        versionLabel={strings("dashboard.switchToClassicUI")}
        onTourClick={startTour}
      />

      {isPreviewMode && (
        <PreviewModeBanner
          previewRole={previewRole || "super_admin"}
          previewWhitelabel={previewWhitelabel}
          savedCity={savedCity}
          citiesList={citiesList}
          onExit={exitPreviewMode}
        />
      )}

      <div className="tw-flex tw-h-[calc(var(--app-vh)-64px)] tw-w-full tw-bg-muted tw-pb-[72px] sm:tw-pb-0 tw-overflow-hidden">
        <DashboardSidebar
          visibleSidebarConfig={visibleSidebarConfig}
          isMobileOpen={isMobileMenuOpen}
          onMobileOpenChange={setIsMobileMenuOpen}
          isTourRunning={isTourRunning}
          isSidebarItemSelected={isSidebarItemSelected}
          onCustomizeClick={handleCustomizeClick}
          currentLanguageLabel={currentLanguageLabel}
        />

        <div className="tw-m-2.5 lg:tw-m-5 tw-grow tw-min-w-0 tw-flex tw-bg-background tw-flex-col tw-overflow-y-auto tw-rounded-t-2xl sm:tw-rounded-2xl">
          <DashboardRoutes
            searchValue={searchValue}
            setSearchValue={handleSetSearchValue}
            clearSearchValue={clearSearchValue}
            setCurrentPageValue={handleSetCurrentPageValue}
            currentPageValue={currentPageValue}
            orderProps={orderProps}
            orderValue={orderValue}
            filterProps={filterProps}
            adminDetails={adminDetails}
            featureFlags={featureFlags}
          />
        </div>
      </div>

      <React.Suspense fallback={null}>
        <OnboardingTour />
      </React.Suspense>

      <SidebarCustomizeDrawer
        open={isCustomizeDrawerOpen}
        onOpenChange={setIsCustomizeDrawerOpen}
        config={orderedSidebarConfig}
        preferences={preferences}
        onPreferencesChange={setPreferences}
      />

      <BottomNavigation onMenuClick={handleMobileMenuClick} />

      <EntityPanel />
    </div>
  );
}

function DashboardScreen() {
  const { pathname } = useLocation();
  const isFleetMap =
    pathname.replace(/\/$/, "") === routes.toV2Route(routes.DASHBOARD_MAP);
  return (
    <QueryClientProvider client={queryClient}>
      <TourProvider>
        <DashboardContent />
      </TourProvider>
      {process.env.NODE_ENV === "development" && !isFleetMap && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default DashboardScreen;
